import { useMemo } from 'react';
import { FilterInputType, FilterType, FilterValue } from 'components/external-reference/Filter';
import { IFilterColumn } from 'components/report-content/components/manage-filters';
import { ReportFilterSegmentValuesData } from 'components/report-content/hooks/useReportContentSettings';
import { prepareSelectFilterData } from 'components/report-content/components/manage-filters/parts/filter/FilterControlSelect';
import { ComponentSettingsManageFilters, ManageFiltersInitialDataResponse, RendererFilterType } from '@report-content';
import { prepareSegments } from 'components/element-viewer';
import buildFilterColumns from 'components/report-content/components/manage-filters/helpers/buildFilterColumns';
function convertColumnData(filterColumn: IFilterColumn) {
    if (filterColumn.filterType == 'range') {
        return { data: filterColumn.savedValues, values: filterColumn.savedValues };
    } else if (filterColumn.filterType == 'charting_interval') {
        return { data: filterColumn.chartingIntervals, values: filterColumn.savedValues };
    }
    return prepareSelectFilterData(filterColumn, false);
}

function convertType(filter: IFilterColumn) {
    // Map Report Viewer filter columns to Reference Manager filters
    const typeMap = new Map<RendererFilterType, FilterInputType>([
        ['dropdown', 'single'],
        ['charting_interval', 'single'],
        ['range', 'range'],
        ['datetime', 'date_range'],
        ['multiselect', 'multi'],
    ]);
    const type: RendererFilterType =
        filter.filterType == 'range' && filter.value_type == 'datetime' ? 'datetime' : filter.filterType;
    return typeMap.get(type) || 'single';
}

export default function useReportFiltersForReference(
    segmentValues: ReportFilterSegmentValuesData,
    segmentValueId: number,
    manageFiltersSettings: ComponentSettingsManageFilters,
    initialData?: ManageFiltersInitialDataResponse,
): { filters: FilterType[]; filterColumns: IFilterColumn[] } {
    return useMemo<{ filters: FilterType[]; filterColumns: IFilterColumn[] }>(() => {
        if (!initialData) {
            return { filters: [], filterColumns: [] };
        }
        const filterColumns = manageFiltersSettings ? buildFilterColumns(manageFiltersSettings, initialData) : [];
        const filtersList: FilterType[] = filterColumns.map((f) => {
            const { data, values } = convertColumnData(f);
            return {
                values: data.map((r) => ({
                    value_id: r.value,
                    value: r.value,
                    display_value: r.label,
                    isVisible: true,
                })),
                visibleInViewer: true,
                replacedBySegment: false,
                filterId: f.dataset_column_id,
                filterInput: convertType(f),
                name: f.reference_name,
                title: f.column_name,
                required: f.include_all_value == 'N',
                filterType: 'regular',
                parentFilterId: 0,
                showTimepicker: f.show_date_only == 'N',
                selectedValues: values,
                defaultValues: [],
                canBeCached: false,
                isSegment: false,
            };
        });

        const segmentFilter: FilterType[] = [];
        segmentValues.segments.forEach((s) => {
            const f = {
                values: s.values.map(
                    (v): FilterValue => ({
                        value_id: v.segment_value_id,
                        value: String(v.segment_value_id),
                        display_value: v.segment,
                        isVisible: true,
                    }),
                ),
                visibleInViewer: true,
                replacedBySegment: true,
                filterId: s.segment_id,
                filterInput: 'single' as FilterInputType,
                name: s.name,
                title: s.name,
                required: false,
                filterType: 'regular',
                parentFilterId: 0,
                showTimepicker: false,
                selectedValues: [segmentValueId],
                defaultValues: [],
                canBeCached: false,
                isSegment: false,
            };
            segmentFilter.push({ ...f, replacedBySegment: false });
            filtersList.push(f);
        });
        const filters = prepareSegments(structuredClone(filtersList), segmentFilter).filtersList;
        return { filters, filterColumns };
    }, [initialData, manageFiltersSettings]);
}
